import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router'
import { useState } from 'react'
import { Button, Descriptions, Dropdown, Empty, Modal, Popover, Progress, Spin, Tag, type MenuProps } from 'antd'
import { BellOutlined, DatabaseOutlined } from '@ant-design/icons'
import { generationModes } from '@/entities/ai-model/model/generation-mode'
import { logoutMember, memberKeys, refreshMemberPoints, useCurrentMember, useFileQuota, useMemberSession, usePointBalance, usePointTransactions, type FileQuota } from '@/entities/member'
import { getTaskPreviewArtifact, getTaskProgressStatus, getTaskPrompt, getTaskStatusColor, GLOBAL_TASK_LIST_PARAMS, isTerminalTaskStatus, taskStatusLabels, useTasks } from '@/entities/generation-task'
import { MemberProfileModal } from '@/widgets/member-center/MemberProfileModal'
import { PointTransactionsModal } from '@/widgets/member-center/PointTransactionsModal'
import { ChangePasswordModal } from '@/widgets/member-center/ChangePasswordModal'
import { BrandLogo } from '@/shared/ui/BrandLogo'
import { queryClient } from '@/app/query-client'
import { useNotificationUnreadCount } from '@/entities/notification'
import { MessageCenterPanel } from '@/widgets/message-center/MessageCenterPanel'
import { useTopActionPanelStore } from '@/shared/model/top-action-panel-store'
import '@/widgets/message-center/message-center.css'

const mainNavigation = [
  { icon: '✦', label: '创作', to: '/app/create/text-to-image' },
  { icon: '▧', label: '资产', to: '/app/assets' },
  { icon: '◷', label: '历史', to: '/app/history' },
  { icon: '◫', label: '任务', to: '/app/tasks' },
  { icon: '⌘', label: '模型', to: '/app/models' },
  { icon: '◇', label: '模板', to: '/app/templates' },
]

const webGuideUrl = 'https://my.feishu.cn/wiki/U6qhwQVkHivuTpkL8QJcB9zpnac'
const webHelpUrl = 'https://my.feishu.cn/wiki/R7cgwDljbi1fqikBcRIcUkzCnld'

function SparkIcon() {
  return <span className="spark-icon" aria-hidden="true">✦</span>
}
function formatRecentTime(value: string) {
  const date = new Date(value)
  const today = new Date()
  const sameDay = date.toDateString() === today.toDateString()
  return date.toLocaleString('zh-CN', sameDay
    ? { hour: '2-digit', minute: '2-digit', hour12: false }
    : { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
}

function getCurrentArea(pathname: string) {
  if (pathname.startsWith('/app/assets')) return { breadcrumb: '内容管理', title: '资产库' }
  if (pathname.startsWith('/app/history')) return { breadcrumb: '创作管理', title: '创作历史' }
  if (pathname.startsWith('/app/tasks')) return { breadcrumb: '创作管理', title: '任务中心' }
  if (pathname.startsWith('/app/models')) return { breadcrumb: '能力中心', title: '模型库' }
  if (pathname.startsWith('/app/templates')) return { breadcrumb: '创作资源', title: '模板广场' }
  return { breadcrumb: 'AI 工作空间', title: 'Witemedia AI Hub' }
}

const quotaFieldLabels: Record<keyof FileQuota, string> = {
  quotaBytes: '总容量',
  usedBytes: '已使用',
  pendingBytes: '处理中',
  occupiedBytes: '已占用',
  availableBytes: '可用',
  overQuotaBytes: '超额',
  overQuota: '是否超额',
}

function formatBytesAsG(bytes?: number) {
  if (typeof bytes !== 'number') return '-'
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)}G`
}

function formatQuotaValue(key: keyof FileQuota, value: FileQuota[keyof FileQuota]) {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (key.endsWith('Bytes')) return formatBytesAsG(value)
  return value.toLocaleString('zh-CN')
}

export function ConsoleLayout() {
  const authenticated = useMemberSession((state) => state.authenticated)
  const clearMemberSession = useMemberSession((state) => state.clear)
  const passwordChangeRequired = useMemberSession((state) => state.passwordChangeRequired)
  const setPasswordChangeRequired = useMemberSession((state) => state.setPasswordChangeRequired)
  const [memberDialog, setMemberDialog] = useState<'profile' | 'points' | 'password' | null>(null)
  const openPopover = useTopActionPanelStore((state) => state.openPanel)
  const setOpenPopover = useTopActionPanelStore((state) => state.setOpenPanel)
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
  const [quotaModalOpen, setQuotaModalOpen] = useState(false)
  const { data: member } = useCurrentMember()
  const { data: points } = usePointBalance()
  const { data: fileQuota, isFetching: fileQuotaLoading, refetch: refetchFileQuota } = useFileQuota()
  const { data: transactions, isLoading: transactionsLoading } = usePointTransactions(1, 5)
  const { data: tasks, isLoading: tasksLoading } = useTasks(GLOBAL_TASK_LIST_PARAMS.page, GLOBAL_TASK_LIST_PARAMS.pageSize, {}, { poll: true })
  const { data: unreadCount } = useNotificationUnreadCount()
  const location = useLocation()
  const navigate = useNavigate()
  const activeMode = generationModes.find((item) => location.pathname.startsWith(item.route)) ?? generationModes[0]!
  const inCreateArea = location.pathname.includes('/app/create/')
  const currentArea = getCurrentArea(location.pathname)

  if (!authenticated) return <Navigate to="/" replace />
  const activeTasks = tasks?.items.filter((task) => !isTerminalTaskStatus(task.status)) ?? []
  const recentTasks = tasks?.items.slice(0, 5) ?? []
  const mustChangePassword = passwordChangeRequired || member?.mustChangePassword === true

  const openMemberDialog = (dialog: Exclude<typeof memberDialog, null>) => {
    setOpenPopover(null)
    setMemberDialog(dialog)
    if (dialog === 'points') void refreshMemberPoints(queryClient)
  }

  const changePointPopoverOpen = (open: boolean) => {
    setOpenPopover(open ? 'points' : null)
    if (open) void refreshMemberPoints(queryClient)
  }

  const openQuotaModal = () => {
    setOpenPopover(null)
    setQuotaModalOpen(true)
    void refetchFileQuota()
  }

  const logout = async () => {
    try {
      await logoutMember()
    } catch {
      // Local session must still be cleared when the server session has already expired.
    } finally {
      setMemberDialog(null)
      setOpenPopover(null)
      clearMemberSession()
      queryClient.clear()
      void navigate('/')
    }
  }

  const memberMenu: MenuProps = {
    items: [
      { key: 'profile', icon: <span>◉</span>, label: '个人资料' },
      { key: 'points', icon: <span>◆</span>, label: '积分流水' },
      { key: 'password', icon: <span>⌁</span>, label: '修改密码' },
      { type: 'divider' },
      { key: 'logout', danger: true, icon: <span>↪</span>, label: '退出登录' },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') void logout()
      else openMemberDialog(key as Exclude<typeof memberDialog, null>)
    },
  }

  const pointPopoverContent = <section className="top-action-panel" aria-label="最近积分流水">
    <div className="top-action-panel-header">
      <span><b>积分流水</b><small>最近 5 条积分变动</small></span>
      <strong><i>◆</i>{points?.balance ?? '-'}</strong>
    </div>
    <div className="top-action-list">
      {transactionsLoading
        ? <div className="top-action-loading"><Spin size="small" /></div>
        : transactions?.items.length
          ? transactions.items.map((item) => <div className="point-message-row" key={item.id}>
            <span><b>{item.transactionTypeName}</b><small>{item.description || item.directionName}</small></span>
            <span className="message-row-side"><strong className={item.direction === 'INCOME' ? 'is-income' : 'is-expense'}>{item.direction === 'INCOME' ? '+' : '-'}{Math.abs(item.changeAmount)}</strong><time>{formatRecentTime(item.createdAt)}</time></span>
          </div>)
          : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无积分流水" />}
    </div>
    <Button type="text" block className="top-action-view-all" onClick={() => openMemberDialog('points')}>查看全部积分流水 <span>→</span></Button>
  </section>

  const taskPopoverContent = <section className="top-action-panel" aria-label="最近任务">
    <div className="top-action-panel-header">
      <span><b>最近任务</b><small>{activeTasks.length ? `${activeTasks.length} 个任务处理中` : '当前没有进行中的任务'}</small></span>
      {activeTasks.length > 0 && <em>{activeTasks.length}</em>}
    </div>
    <div className="top-action-list">
      {tasksLoading
        ? <div className="top-action-loading"><Spin size="small" /></div>
        : recentTasks.length
          ? recentTasks.map((task) => <div className="task-message-row" key={task.taskNo}>
            <div className="task-message-main">
              <span><b title={getTaskPrompt(task)}>{getTaskPrompt(task)}</b><small>{formatRecentTime(task.createdAt)}</small></span>
              <Tag color={getTaskStatusColor(task.status)}>{taskStatusLabels[task.status]}</Tag>
            </div>
            {!isTerminalTaskStatus(task.status) && <Progress percent={task.progress} size="small" showInfo={false} status={getTaskProgressStatus(task.status)} />}
          </div>)
          : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无生成任务" />}
    </div>
    <Button type="text" block className="top-action-view-all" onClick={() => { setOpenPopover(null); void navigate('/app/tasks') }}>查看全部任务 <span>→</span></Button>
  </section>

  return <div className="app-shell">
    <aside className="sidebar">
      <NavLink className="brand" to="/app/create/text-to-image" aria-label="Witemedia 首页"><BrandLogo compact /></NavLink>
      <nav className="main-nav" aria-label="主导航">
        {mainNavigation.map((item) => <NavLink key={item.label} to={item.to} className={({ isActive }) => `nav-item ${isActive || (item.label === '创作' && inCreateArea) ? 'active' : ''}`}><span className="nav-icon">{item.icon}</span><span>{item.label}</span></NavLink>)}
      </nav>
      <div className="sidebar-bottom">
        <Button type="text" className="nav-item sidebar-message-button" aria-label={`打开消息中心，${unreadCount?.count ?? 0} 条未读`} onClick={() => setMessageDrawerOpen(true)}><BellOutlined className="nav-icon" /><span>消息</span>{Boolean(unreadCount?.count) && <em>{Math.min(unreadCount?.count ?? 0, 99)}</em>}</Button>
        <Button type="text" className="nav-item" href={webHelpUrl} target="_blank" rel="noreferrer noopener"><span className="nav-icon">?</span><span>帮助</span></Button><Dropdown menu={memberMenu} trigger={['click']} placement="topLeft" overlayClassName="member-avatar-menu"><Button type="text" shape="circle" className="avatar" aria-label="打开用户菜单">{(member?.nickname || member?.email || 'U').slice(0, 1).toUpperCase()}</Button></Dropdown></div>
    </aside>

    {inCreateArea && <aside className="create-subnav">
      <div className="subnav-heading"><span>CREATE</span><h2>创作中心</h2><p>选择创作工具开始生产</p></div>
      <nav aria-label="创作工具">
        {generationModes.map((item) => <NavLink key={item.type} to={item.route} className={({ isActive }) => isActive ? 'active' : ''}><span className="module-icon">{item.icon}</span><span><b>{item.name}</b><small>{item.description}</small></span><i>›</i></NavLink>)}
      </nav>
      <div className="subnav-guide"><SparkIcon /><b>创作指南</b><p>了解不同工具的适用场景和提示词技巧</p><Button type="link" href={webGuideUrl} target="_blank" rel="noreferrer noopener">查看指南 →</Button></div>
    </aside>}

    <main className={`workspace ${inCreateArea ? '' : 'workspace-wide'}`}>
      <header className="topbar"><div><div className="breadcrumb"><span>{inCreateArea ? '创作中心' : currentArea.breadcrumb}</span><b>/</b><span>{inCreateArea ? activeMode.name : currentArea.title}</span></div><h1>{inCreateArea ? activeMode.title : currentArea.title}</h1></div><div className="top-actions"><div className="service-state"><i />服务运行正常</div><Popover trigger="click" placement="bottomRight" arrow={false} open={openPopover === 'points'} onOpenChange={changePointPopoverOpen} content={pointPopoverContent} rootClassName="top-action-popover"><Button className="credits" aria-label="查看最近积分流水"><span>◆</span><b>{points?.balance ?? '-'}</b><span className="top-action-label">积分</span></Button></Popover><Button className="quota-button" aria-label="查看素材容量" loading={fileQuotaLoading && !quotaModalOpen} onClick={openQuotaModal}><DatabaseOutlined /><b>{formatBytesAsG(fileQuota?.availableBytes)}</b><span className="top-action-label">素材容量</span></Button><Popover trigger="click" placement="bottomRight" arrow={false} open={openPopover === 'tasks'} onOpenChange={(open) => setOpenPopover(open ? 'tasks' : null)} content={taskPopoverContent} rootClassName="top-action-popover"><Button className="queue-button" aria-label="查看最近任务"><span>◫</span><span className="top-action-label">任务队列</span><em>{activeTasks.length}</em></Button></Popover></div></header>
      <Outlet />
      {inCreateArea && activeTasks[0] && <footer className="task-strip"><div className="task-info">{getTaskPreviewArtifact(activeTasks[0])?.previewUrl ? <img className="task-thumb" src={getTaskPreviewArtifact(activeTasks[0])?.previewUrl ?? ''} alt={getTaskPrompt(activeTasks[0])} loading="lazy" /> : <span className="task-thumb"><SparkIcon /></span>}<div><b title={getTaskPrompt(activeTasks[0])}>{getTaskPrompt(activeTasks[0])}</b><small><i /> {taskStatusLabels[activeTasks[0].status]}</small></div></div><div className="task-progress"><div><span style={{ width: `${activeTasks[0].progress}%` }} /></div><b>{activeTasks[0].progress}%</b></div><Button href="/app/tasks">查看任务</Button></footer>}
    </main>
    <MessageCenterPanel open={messageDrawerOpen} unreadCount={unreadCount?.count ?? 0} onClose={() => setMessageDrawerOpen(false)} />
    <MemberProfileModal open={memberDialog === 'profile'} onClose={() => setMemberDialog(null)} onAccountDeleted={logout} />
    <PointTransactionsModal open={memberDialog === 'points'} onClose={() => setMemberDialog(null)} />
    <Modal open={quotaModalOpen} title="素材容量" width={640} footer={null} onCancel={() => setQuotaModalOpen(false)}>
      <Spin spinning={fileQuotaLoading}>
        {fileQuota ? <Descriptions bordered column={1} size="small" items={(Object.keys(quotaFieldLabels) as Array<keyof FileQuota>).map((key) => ({
          key,
          label: quotaFieldLabels[key],
          children: formatQuotaValue(key, fileQuota[key]),
        }))} /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无素材容量数据" />}
      </Spin>
    </Modal>
    <ChangePasswordModal
      open={memberDialog === 'password' || mustChangePassword}
      force={mustChangePassword}
      onClose={() => setMemberDialog(null)}
      onLogout={logout}
      onChanged={async () => {
        setPasswordChangeRequired(false)
        await queryClient.invalidateQueries({ queryKey: memberKeys.me() })
      }}
    />
  </div>
}
